import QrPhotoUploadModalTrigger from '../components/modals/QrPhotoUploadModalTrigger';
import accessUtils from '../utils/accessUtils';

import { useGetListingDetails } from './queries/properties';

import { useQrPhotoUploadPollSession } from './queries/useQrPhotoUploadPolling';
import { useIsPhotoUploadRestricted } from './useIsPhotoUploadRestricted';

/**
 * The phone photo upload entry point for a `PhotoUploadFormField`, or undefined when the listing cannot
 * offer it.
 *
 * Owns the multifamily exclusion, the upload restriction and the poll window that refreshes photos
 * while the generated link is live, so each surface that offers photo upload wires it the same way.
 * With a multiplex building session, the link uploads to every unit in it and sits behind its own flag.
 */
export const useQrPhotoUploadEntryPoint = (
  listingAlias: string,
  isDisabled = false,
  multiplexBuildingSessionId?: string,
) => {
  const { data: listingDetails } = useGetListingDetails(listingAlias);
  const { startQrUploadPollWindow } = useQrPhotoUploadPollSession(listingAlias, multiplexBuildingSessionId);
  const isUploadRestricted = useIsPhotoUploadRestricted(listingAlias);

  // Withhold the entry point until the listing is known, whether details are still loading or the
  // fetch failed: appearing and then vanishing on a multifamily listing reads worse than showing up
  // a moment late, and the multifamily exclusion is permanent, so unknown has to mean ineligible.
  // The query only runs with an alias, so without one there is nothing to wait for.
  const isListingUnknown = Boolean(listingAlias) && listingDetails === undefined;

  if (isListingUnknown || listingDetails?.isMultifamily) {
    return undefined;
  }

  if (multiplexBuildingSessionId && !accessUtils.canAccessQrPhotoUploadMultiplex()) {
    return undefined;
  }

  return (variant: 'tile' | 'panel') => (
    <QrPhotoUploadModalTrigger
      variant={variant}
      isDisabled={isDisabled || isUploadRestricted}
      multiplexBuildingSessionId={multiplexBuildingSessionId}
      onUploadUrlGenerated={startQrUploadPollWindow}
    />
  );
};
